// Import API configuration from the centralized config
import { API_URL, API_BASE } from '@/config/api.config';

// Re-export for backward compatibility
export { API_URL, API_BASE };

// Export API_URL for direct fetch usage in components
export const getApiUrl = (path: string = '') => `${API_BASE}${path}`;

// Legacy API URL (for migration - will be removed)
export const getLegacyApiUrl = (path: string = '') => `${API_URL}${path}`;

export interface ApiResponse<T> {
  data?: T;
  error?: string;
}

// CSRF token storage (in memory, not localStorage for security)
let csrfToken: string | null = null;

export function setCsrfToken(token: string) {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

// Helper function to get CSRF token from cookie (exported for components)
export function getCsrfTokenFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? match[1] : null;
}

/**
 * Create headers for authenticated fetch requests
 * Use this for direct fetch calls outside the API wrapper
 */
export function getAuthHeaders(method: string = 'GET'): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  // Add CSRF token for state-changing requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
    const token = csrfToken || getCsrfTokenFromCookie();
    if (token) {
      headers['x-csrf-token'] = token;
    }
  }
  
  return headers;
}

/**
 * Create fetch options for authenticated requests
 * Use this for direct fetch calls outside the API wrapper
 */
export function getAuthFetchOptions(method: string = 'GET', body?: any): RequestInit {
  return {
    method,
    headers: getAuthHeaders(method),
    credentials: 'include', // Include cookies for auth
    ...(body ? { body: JSON.stringify(body) } : {}),
  };
}

// Helper function for exponential backoff retry
async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
  retries = 3
): Promise<ApiResponse<T>> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add CSRF token for state-changing requests
  const method = options.method?.toUpperCase() || 'GET';
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const token = csrfToken || getCsrfTokenFromCookie();
    if (token) {
      headers['x-csrf-token'] = token;
    }
  }

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include', // Include cookies with every request
      });

      const data = await response.json();

      // Retry on rate limit with exponential backoff
      if (response.status === 429 && attempt < retries) {
        const waitTime = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
        console.log(`Rate limited. Retrying in ${waitTime}ms... (attempt ${attempt + 1}/${retries})`);
        await sleep(waitTime);
        continue;
      }

      if (!response.ok) {
        return { error: data.error || 'An error occurred' };
      }

      return { data };
    } catch (error) {
      if (attempt === retries) {
        return { error: 'Network error. Please try again.' };
      }
      // Retry on network errors too
      const waitTime = Math.pow(2, attempt) * 1000;
      await sleep(waitTime);
    }
  }

  return { error: 'Request failed after retries.' };
}

// Auth
export const auth = {
  register: (data: { 
    email: string; 
    password: string; 
    name: string;
    companyName?: string;
    companyCountry?: string;
    companySector?: string;
    selectedPlan?: string;
    verificationToken?: string;
  }) =>
    fetchApi<{ user: any; csrfToken: string }>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  login: (data: { email: string; password: string }) =>
    fetchApi<{ user: any; csrfToken: string }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  logout: () =>
    fetchApi('/api/v1/auth/logout', {
      method: 'POST',
    }),

  fetchCsrfToken: async () => {
    const result = await fetchApi<{ csrfToken: string }>('/api/v1/auth/csrf-token');
    if (result.data?.csrfToken) {
      setCsrfToken(result.data.csrfToken);
    }
    return result;
  },
  
  me: () => fetchApi('/api/v1/auth/me'),
};

// Organizations
export const organizations = {
  list: () => fetchApi('/api/v1/organizations'),
  
  get: (id: string) => fetchApi(`/api/v1/organizations/${id}`),
  
  create: (data: { name: string; sector: string; country: string }) =>
    fetchApi('/api/v1/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: { 
    name: string; 
    sector: string; 
    country: string;
    contactEmail?: string;
    contactPhone?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    websiteUrl?: string;
    registrationNo?: string;
    vatNumber?: string;
    dpoName?: string;
    dpoEmail?: string;
  }) =>
    fetchApi(`/api/v1/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    fetchApi(`/api/v1/organizations/${id}`, {
      method: 'DELETE',
    }),
};

// Projects
export const projects = {
  list: (organizationId: string) =>
    fetchApi(`/api/v1/projects/organization/${organizationId}`),
  
  get: (id: string) => fetchApi(`/api/v1/projects/${id}`),
  
  create: (data: { organizationId: string; name: string; description?: string }) =>
    fetchApi('/api/v1/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: { name: string; description?: string; framework?: string }) =>
    fetchApi(`/api/v1/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    fetchApi(`/api/v1/projects/${id}`, {
      method: 'DELETE',
    }),
};

// Assets
export const assets = {
  list: (projectId: string) =>
    fetchApi(`/api/v1/assets/project/${projectId}`),
  
  create: (data: any) =>
    fetchApi('/api/v1/assets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: any) =>
    fetchApi(`/api/v1/assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  bulkCreate: (data: any) =>
    fetchApi('/api/v1/assets/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  bulkUpload: async (projectId: string, file: File): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    
    try {
      const headers: HeadersInit = {};
      // Add CSRF token for state-changing requests
      const token = csrfToken || getCsrfTokenFromCookie();
      if (token) {
        headers['x-csrf-token'] = token;
      }
      
      const response = await fetch(`${API_URL}/api/v1/assets/upload`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include', // Include cookies
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return { error: data.error || 'Upload failed' };
      }
      
      return { data };
    } catch (error) {
      return { error: 'Network error. Please try again.' };
    }
  },
  
  delete: (id: string) =>
    fetchApi(`/api/v1/assets/${id}`, {
      method: 'DELETE',
    }),
};

// Scan
export const scan = {
  run: (projectId: string) =>
    fetchApi(`/api/v1/scan/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};

// Reports
export const reports = {
  get: (projectId: string) =>
    fetchApi(`/api/v1/reports/${projectId}`),
  
  exportJson: async (projectId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/reports/${projectId}/export/json`, {
        credentials: 'include', // Include cookies
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${projectId}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export JSON failed:', error);
      alert(error instanceof Error ? error.message : 'Failed to export JSON');
    }
  },
  
  exportPdf: async (projectId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/reports/${projectId}/export/pdf`, {
        credentials: 'include', // Include cookies
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export PDF failed:', error);
      alert(error instanceof Error ? error.message : 'Failed to export PDF');
    }
  },
};

// Framework Records
export const frameworkRecords = {
  list: (projectId: string) =>
    fetchApi(`/api/v1/framework-records/project/${projectId}`),
  
  get: (id: string) =>
    fetchApi(`/api/v1/framework-records/${id}`),
  
  stats: (projectId: string) =>
    fetchApi(`/api/v1/framework-records/stats/${projectId}`),
  
  available: () =>
    fetchApi('/api/v1/framework-records/available'),
  
  create: (projectId: string, data: Record<string, string>) =>
    fetchApi('/api/v1/framework-records', {
      method: 'POST',
      body: JSON.stringify({ projectId, data }),
    }),
  
  update: (id: string, data: { data?: Record<string, string>; status?: string }) =>
    fetchApi(`/api/v1/framework-records/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    fetchApi(`/api/v1/framework-records/${id}`, {
      method: 'DELETE',
    }),
  
  bulkDelete: async (ids: string[]) => {
    // Delete records one by one - backend doesn't have bulk delete endpoint yet
    const results = await Promise.all(
      ids.map(id => fetchApi(`/api/v1/framework-records/${id}`, { method: 'DELETE' }))
    );
    const failed = results.filter(r => r.error);
    if (failed.length > 0) {
      return { error: `Failed to delete ${failed.length} records` };
    }
    return { data: { deleted: ids.length } };
  },
};
